import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner, Navbar, Image, ProgressBar } from 'react-bootstrap';
import { useUserAuth } from '../context/UserAuthContext';

const UserRegister: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const { register, isLoading, error, isAuthenticated, clearError } = useUserAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/user/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  // Calculate password strength
  useEffect(() => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    setPasswordStrength(strength);
  }, [password]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccess(null);
    clearError();

    // Validation
    if (!email || !password || !confirmPassword) {
      setLocalError('Please fill in all required fields');
      return;
    }

    if (!email.includes('@')) {
      setLocalError('Please enter a valid email address');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setLocalError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    const finalDisplayName = displayName.trim() || email.split('@')[0];

    try {
      await register(email, password, finalDisplayName);
      setSuccess('Registration successful! Redirecting to login...');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/user/login');
      }, 2000);
    } catch (err: any) {
      // Error is already set in context
      console.error('Registration error:', err);
    }
  };

  const displayError = localError || error;

  const getPasswordStrengthVariant = () => {
    if (passwordStrength < 50) return 'danger';
    if (passwordStrength < 75) return 'warning';
    return 'success';
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength === 0) return '';
    if (passwordStrength < 50) return 'Weak';
    if (passwordStrength < 75) return 'Medium';
    if (passwordStrength < 100) return 'Good';
    return 'Strong';
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      {/* Header */}
      <Navbar bg="white" variant="light" className="mb-4 border-bottom shadow-sm">
        <Container>
          <Navbar.Brand>
            <Image 
              src="/qualrisk-logo.webp" 
              alt="QualRisk AI" 
              width="167" 
              height="30" 
              className="d-inline-block align-top me-2" 
            />
            QAI Secure File Transfer Manager
          </Navbar.Brand>
        </Container>
      </Navbar>

      {/* Main Content */}
      <Container className="flex-grow-1 d-flex align-items-center justify-content-center py-4">
        <Card style={{ width: '100%', maxWidth: '500px' }} className="shadow">
          <Card.Body className="p-4">
            <div className="text-center mb-4">
              <h3 className="mb-2">Create Account</h3>
              <p className="text-muted">Register for file upload access</p>
            </div>

            {success && (
              <Alert variant="success">
                <i className="bi bi-check-circle-fill me-2"></i>
                {success}
              </Alert>
            )}

            {displayError && (
              <Alert variant="danger" dismissible onClose={() => { setLocalError(null); clearError(); }}>
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {displayError}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                  autoFocus
                />
                <Form.Text className="text-muted">
                  We'll use this for your login and notifications
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="displayName">
                <Form.Label>Display Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your name (optional)"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  disabled={isLoading}
                />
                <Form.Text className="text-muted">
                  This will be shown in your account
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="password">
                <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
                {password && (
                  <div className="mt-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <small className="text-muted">Password strength:</small>
                      <small className={`text-${getPasswordStrengthVariant()}`}>
                        {getPasswordStrengthLabel()}
                      </small>
                    </div>
                    <ProgressBar 
                      now={passwordStrength} 
                      variant={getPasswordStrengthVariant()}
                      style={{ height: '4px' }}
                    />
                  </div>
                )}
                <Form.Text className="text-muted">
                  Min 8 characters with uppercase, lowercase, and number
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4" controlId="confirmPassword">
                <Form.Label>Confirm Password <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
                {password && confirmPassword && (
                  <Form.Text className={password === confirmPassword ? 'text-success' : 'text-danger'}>
                    {password === confirmPassword ? (
                      <>✓ Passwords match</>
                    ) : (
                      <>✗ Passwords do not match</>
                    )}
                  </Form.Text>
                )}
              </Form.Group>

              <Button 
                variant="primary" 
                type="submit" 
                className="w-100 mb-3"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Creating account...
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-plus me-2"></i>
                    Create Account
                  </>
                )}
              </Button>

              <div className="text-center">
                <small className="text-muted">
                  Already have an account?{' '}
                  <Link to="/user/login" className="text-decoration-none">
                    Sign in here
                  </Link>
                </small>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>

      {/* Footer */}
      <footer className="bg-light py-3">
        <Container>
          <div className="text-center text-muted">
            &copy; {new Date().getFullYear()} QAI Secure File Transfer Manager
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default UserRegister;

